<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Documentation Content -->
    <div class="max-w-4xl mx-auto">
      <!-- Page Header -->
      <div class="mb-8">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-50">
              {{ pageTitle }}
            </h1>
            <p v-if="pageDescription" class="text-lg text-gray-600 dark:text-gray-300 mt-2">
              {{ pageDescription }}
            </p>
          </div>
        </div>
        
        <!-- Breadcrumb Navigation -->
        <nav class="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
          <router-link to="/" class="hover:text-gray-700 dark:hover:text-gray-300">Home</router-link>
          <span>/</span>
          <router-link to="/docs" class="hover:text-gray-700 dark:hover:text-gray-300">Documentation</router-link>
          <span v-if="category" v-html="'/'"></span>
          <span v-if="category" class="text-gray-700 dark:text-gray-300">{{ category }}</span>
          <span v-if="section" v-html="'/'"></span>
          <span v-if="section" class="text-gray-700 dark:text-gray-300">{{ section }}</span>
        </nav>
      </div>

      <!-- Content Area -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <!-- Content Header -->
        <div v-if="contentHeader" class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-50">
                {{ contentHeader.title }}
              </h2>
              <p v-if="contentHeader.description" class="text-gray-600 dark:text-gray-300 mt-1">
                {{ contentHeader.description }}
              </p>
            </div>
            <div v-if="contentHeader.meta" class="text-right text-sm text-gray-500 dark:text-gray-400">
              <div v-if="contentHeader.meta.lastUpdated">
                Last updated: {{ formatDate(contentHeader.meta.lastUpdated) }}
              </div>
              <div v-if="contentHeader.meta.readTime">
                Read time: {{ contentHeader.meta.readTime }} min
              </div>
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div class="p-6">
          <div v-if="isLoading" class="text-center py-12">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-4 text-gray-600 dark:text-gray-400">Loading documentation...</p>
          </div>

          <div v-else-if="error" class="text-center py-12">
            <div class="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">Error Loading Content</h3>
            <p class="text-gray-600 dark:text-gray-400 mb-4">{{ error }}</p>
            <button @click="retryLoad" class="btn-primary">Try Again</button>
          </div>

          <div v-else-if="content" class="prose prose-lg prose-content max-w-none">
            <!-- Render the content based on type -->
            <div v-if="content.type === 'markdown'">
              <!-- Use the Enhanced Markdown Renderer for full functionality -->
              <EnhancedMarkdownRenderer 
                :content="content.rawContent" 
                :enable-syntax-highlighting="true"
                :enable-collapsible-trees="true"
              />
            </div>
            <div v-else-if="content.type === 'text'" class="whitespace-pre-wrap">{{ content.text }}</div>
            <div v-else class="text-gray-600 dark:text-gray-400">
              Content type not supported: {{ content.type }}
            </div>
          </div>

          <div v-else class="text-center py-12">
            <div class="text-gray-400 text-6xl mb-4">📄</div>
            <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">No Content Found</h3>
            <p class="text-gray-600 dark:text-gray-400">
              The requested documentation content could not be found.
            </p>
          </div>
        </div>
      </div>

      <!-- Navigation Footer -->
      <div class="mt-8 flex items-center justify-between">
        <button
          v-if="previousPage"
          @click="navigateTo(previousPage)"
          class="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          <span>←</span>
          <span>Previous</span>
        </button>
        <div v-else></div>

        <button
          v-if="nextPage"
          @click="navigateTo(nextPage)"
          class="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          <span>Next</span>
          <span>→</span>
        </button>
        <div v-else></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { marked } from 'marked'
import EnhancedMarkdownRenderer from '@/components/ui/EnhancedMarkdownRenderer.vue'

import { useDocumentationStore } from '@/stores/documentation'





// Route parameters
const route = useRoute()

// Extract route parameters
const categorySlug = computed(() => {
  // Handle named routes for specific categories first
  if (route.name === 'information') return 'information'
  if (route.name === 'standards') return 'standards'
  if (route.name === 'documentation-section') return 'documentation'

  // Prefer explicit category param when present (generic route)
  const categoryParam = route.params.category as string | undefined
  if (categoryParam) return categoryParam

  // Fallback to path extraction
  const pathParts = route.path.split('/')
  if (pathParts.length >= 3 && pathParts[1] === 'docs') {
    return pathParts[2]
  }

  return undefined as unknown as string
})

const sectionSlug = computed(() => {
  const sectionParam = route.params.section
  
  // Handle string params (from :section pattern)
  if (typeof sectionParam === 'string') {
    return sectionParam
  }
  
  // Fallback to path extraction
  const pathParts = route.path.split('/')
  if (pathParts.length >= 4 && pathParts[1] === 'docs') {
    return pathParts[3]
  }
  
  return undefined
})

// Reactive data
const isLoading = ref(false)
const error = ref<string | null>(null)
const content = ref<any>(null)
const currentCategory = ref<any>(null)
const currentSection = ref<any>(null)

// Store
const documentationStore = useDocumentationStore()

// Computed properties
const pageTitle = computed(() => {
  if (currentSection.value) {
    return currentSection.value.title
  }
  if (currentCategory.value) {
    return currentCategory.value.title
  }
  return 'Documentation'
})

const pageDescription = computed(() => {
  if (currentSection.value) {
    return currentSection.value.summary
  }
  if (currentCategory.value) {
    return currentCategory.value.description
  }
  return ''
})

const category = computed(() => {
  if (currentCategory.value) {
    return currentCategory.value.title
  }
  return ''
})

const section = computed(() => {
  if (currentSection.value) {
    return currentSection.value.title
  }
  return ''
})

const contentHeader = computed(() => {
  if (!currentSection.value && !currentCategory.value) return null
  
  return {
    title: pageTitle.value,
    description: pageDescription.value,
    meta: {
      lastUpdated: currentSection.value?.lastUpdated || currentCategory.value?.lastUpdated,
      readTime: currentSection.value?.estimatedReadTime || 5
    }
  }
})


// Navigation
const previousPage = computed(() => {
  if (!currentCategory.value) return null
  
  if (currentSection.value) {
    // Find previous section in current category
    const sections = currentCategory.value.sections
    const currentIndex = sections.findIndex((s: any) => s.slug === currentSection.value.slug)
    
    if (currentIndex > 0) {
      return {
        path: `/docs/${categorySlug.value}/${sections[currentIndex - 1].slug}`,
        title: sections[currentIndex - 1].title
      }
    } else if (currentIndex === 0) {
      // First section, go to category overview
      return {
        path: `/docs/${categorySlug.value}`,
        title: currentCategory.value.title
      }
    }
  }
  
  // No current section, find previous category
  const categories = documentationStore.documentation
  const currentIndex = categories.findIndex((c: any) => c.slug === categorySlug.value)
  
  if (currentIndex > 0) {
    const prevCategory = categories[currentIndex - 1]
    return {
      path: `/docs/${prevCategory.slug}`,
      title: prevCategory.title
    }
  }
  
  return null
})

const nextPage = computed(() => {
  if (!currentCategory.value) return null
  
  if (currentSection.value) {
    // Find next section in current category
    const sections = currentCategory.value.sections
    const currentIndex = sections.findIndex((s: any) => s.slug === currentSection.value.slug)
    
    if (currentIndex < sections.length - 1) {
      return {
        path: `/docs/${categorySlug.value}/${sections[currentIndex + 1].slug}`,
        title: sections[currentIndex + 1].title
      }
    }
  }
  
  // Find next category
  const categories = documentationStore.documentation
  const currentIndex = categories.findIndex((c: any) => c.slug === categorySlug.value)
  
  if (currentIndex < categories.length - 1) {
    const nextCategory = categories[currentIndex + 1]
    return {
      path: `/docs/${nextCategory.slug}`,
      title: nextCategory.title
    }
  }
  
  return null
})

// Load documentation content
const loadContent = async () => {
  try {
    isLoading.value = true
    error.value = null
    
    // Debug route parameters
    console.log('Route debug:', {
      path: route.path,
      name: route.name,
      params: route.params,
      categorySlug: categorySlug.value,
      sectionSlug: sectionSlug.value
    })
    
    // Always reload documentation to ensure fresh content
    await documentationStore.loadDocumentationFromMarkdown()
    
    // Get the category
    const category = documentationStore.getCategoryBySlug(categorySlug.value)
    if (!category) {
      error.value = `Category "${categorySlug.value}" not found. Available categories: ${documentationStore.documentation.map(c => c.slug).join(', ')}`
      return
    }
    
    currentCategory.value = category

    if (sectionSlug.value) {
      // Try to find the section by exact slug match first
      let section = documentationStore.getSectionBySlug(categorySlug.value, sectionSlug.value)
      
      // If not found, try to find it manually in the category
      if (!section) {
        section = category.sections.find((sec: any) => sec.slug === sectionSlug.value)
      }
      
      if (section) {
        currentSection.value = section
        

        
        // Check if content exists
        if (!section.content) {
          console.error(`Section content is empty for ${section.title} (${section.slug})`)
          error.value = 'Section content is empty or not loaded properly'
          return
        }
        
        // Process MarkdownDirectory components BEFORE converting to HTML
        let processedMarkdown = section.content
        
        // Replace MarkdownDirectory components with Vue component placeholders
        const markdownDirectoryRegex = /<MarkdownDirectory[^>]*title\s*=\s*["']([^"']*)["'][^>]*>([\s\S]*?)<\/MarkdownDirectory>/gi
        
        processedMarkdown = processedMarkdown.replace(
          markdownDirectoryRegex,
          (_match: string, title: string, content: string) => {
            // Sanitize title and content to prevent XSS
            const safeTitle = title ? title.replace(/[<>]/g, '') : 'Directory Structure'
            const safeContent = content ? content.replace(/[<>]/g, '') : 'No content available'
            
            // Create a placeholder that will be replaced with the Vue component
            return `\n\n<DirectoryTreePlaceholder title="${safeTitle}" content="${encodeURIComponent(safeContent)}" />\n\n`
          }
        )
        
                    // First convert the markdown to HTML (without processing tree blocks)
            const html = await marked.parse(processedMarkdown)
            
            // Debug: Check what HTML was generated
            console.log('🔍 After marked.parse:')
            console.log('  HTML contains tree-display:', html.includes('tree-display'))
            console.log('  HTML contains tree-item:', html.includes('tree-item'))
            console.log('  HTML preview:', `${html.substring(0, 500)  }...`)
            
            content.value = {
              type: 'markdown',
              html,
              rawContent: processedMarkdown // Pass the raw markdown with tree blocks to the renderer
            }
      } else {
        error.value = `Section "${sectionSlug.value || 'unknown'}" not found in category "${categorySlug.value}"`
        return
      }
    } else {
      // Show category overview
      currentSection.value = null
      content.value = {
        type: 'markdown',
        html: await marked.parse(`# ${category.title}\n\n${category.description || 'No description available.'}`),
        rawContent: `# ${category.title}\n\n${category.description || 'No description available.'}`
      }
    }

    // Update store current values
    documentationStore.setCurrentCategory(categorySlug.value)
    documentationStore.setCurrentSection(sectionSlug.value || '')

    // Apply syntax highlighting after content is loaded
    await applySyntaxHighlighting()
    
    // Also apply syntax highlighting after a short delay to ensure DOM is fully rendered
    setTimeout(() => {
      applySyntaxHighlighting()
    }, 100)
  } catch (err) {
    console.error('Error loading documentation content:', err)
    error.value = 'Failed to load documentation content'
  } finally {
    isLoading.value = false
  }
}

// Apply syntax highlighting to code blocks
const applySyntaxHighlighting = async () => {
  try {
    // This would be where you apply syntax highlighting
    // For now, we'll just log that it's called
  } catch (error) {
    console.error('Error applying syntax highlighting:', error)
  }
}

// Retry loading content
const retryLoad = () => {
  loadContent()
}

// Navigation function
const navigateTo = (page: any) => {
  if (page && page.path) {
    window.location.href = page.path
  }
}

// Watch for route changes
watch(
  () => [route.params.category, route.params.section],
  () => {
    loadContent()
  },
  { immediate: true }
)

// Load content on mount
onMounted(() => {
  loadContent()
})

// Format date helper
const formatDate = (dateString: string) => {
  if (!dateString) return ''
  
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  } catch (error) {
    console.error('Error formatting date:', error)
    return dateString
  }
}
</script>

<style scoped>
.btn-primary {
  @apply px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors;
}

/* Dark mode adjustments */
:global(.dark) .prose {
  color: #d1d5db;
}

:global(.dark) .prose h1,
:global(.dark) .prose h2,
:global(.dark) .prose h3,
:global(.dark) .prose h4,
:global(.dark) .prose h5,
:global(.dark) .prose h6 {
  color: #f9fafb;
}

:global(.dark) .prose strong {
  color: #f3f4f6;
}

:global(.dark) .prose code {
  background-color: #374151;
  color: #f3f4f6;
}

:global(.dark) .prose pre {
  background-color: #1f2937;
  border-color: #374151;
  }
</style>